/**
 * Optical metrics along a 1-D profile: statistics, uniformity, baselines,
 * multi-peak detection with prominence, full width at arbitrary fractions of
 * maximum (FWHM, FW10%, 1/e^2), edge rise distance, centroid, integral and
 * derivative.
 */
import { basicStats, finiteValues, percentileInPlace } from './stats';

export type BaselineMode = 'zero' | 'min' | 'edges' | 'edgesLinear' | 'percentile' | 'manual';

export interface MetricSettings {
  baseline: BaselineMode;
  /** Fraction (0..0.5) of samples at each end used by the 'edges' baselines and edge levels. */
  edgeFraction: number;
  /** Percentile (0..100) for the 'percentile' baseline. */
  baselinePercentile: number;
  manualBaseline: number;
  /** Analyse maxima ('peak') or minima ('dip'). */
  polarity: 'peak' | 'dip';
  peakMode: 'highest' | 'all';
  /** Minimum peak prominence as a fraction of the profile range. */
  minProminence: number;
  /** Minimum distance between reported peaks, in profile distance units. */
  minSeparation: number;
  /** Width levels as fractions of peak height above baseline (0.5 = FWHM). */
  widthLevels: number[];
  /** Edge transition levels (fractions), e.g. 0.1 / 0.9. */
  edgeLow: number;
  edgeHigh: number;
  /** Gaussian smoothing sigma (samples) applied before differentiation. */
  derivativeSmoothing: number;
}

export const DEFAULT_METRICS: MetricSettings = {
  baseline: 'min',
  edgeFraction: 0.1,
  baselinePercentile: 5,
  manualBaseline: 0,
  polarity: 'peak',
  peakMode: 'highest',
  minProminence: 0.1,
  minSeparation: 0,
  widthLevels: [0.5, 0.1, 0.1353352832366127],
  edgeLow: 0.1,
  edgeHigh: 0.9,
  derivativeSmoothing: 0,
};

/** Baseline as a straight line b(x) = a + slope * x. */
export interface Baseline {
  a: number;
  slope: number;
  mode: BaselineMode;
}

export function baselineAt(b: Baseline, x: number): number {
  return b.a + b.slope * x;
}

export interface WidthResult {
  level: number;
  /** Absolute value of the crossing level. */
  value: number;
  left: number | null;
  right: number | null;
  width: number | null;
}

export interface PeakResult {
  index: number;
  /** Peak position, refined to sub-sample precision by 3-point parabolic interpolation. */
  x: number;
  /** Interpolated peak value (the vertex of the parabola). */
  value: number;
  /** Height above (or depth below, for dips) the baseline. */
  height: number;
  prominence: number;
  widths: WidthResult[];
}

export interface EdgeResult {
  direction: 'rising' | 'falling';
  low: number;
  high: number;
  x10: number | null;
  x50: number | null;
  x90: number | null;
  /** Distance between the low and high crossings (e.g. 10-90 % rise distance). */
  distance: number | null;
  maxSlope: number;
}

export interface ProfileMetrics {
  n: number;
  nValid: number;
  min: number;
  max: number;
  xMin: number;
  xMax: number;
  mean: number;
  sd: number;
  median: number;
  rms: number;
  /** Trapezoidal integral over distance (skipping gaps). */
  integral: number;
  cv: number;
  michelson: number;
  /** Overall uniformity U0 = Lmin / Lmean (EN 13201). */
  u0: number;
  /** Uniformity Lmin / Lmax. */
  u1: number;
  /** (max - min) / mean. */
  nonUniformity: number;
  centroid: number;
  baseline: Baseline;
  peaks: PeakResult[];
  edge: EdgeResult | null;
  clippedSamples: number;
}

/** Linear least-squares fit y = a + b x. */
export function linearFit(xs: ArrayLike<number>, ys: ArrayLike<number>): { a: number; b: number } {
  let n = 0;
  let sx = 0;
  let sy = 0;
  let sxx = 0;
  let sxy = 0;
  for (let i = 0; i < xs.length; i++) {
    const x = xs[i];
    const y = ys[i];
    if (!Number.isFinite(x) || !Number.isFinite(y)) continue;
    n++;
    sx += x;
    sy += y;
    sxx += x * x;
    sxy += x * y;
  }
  const d = n * sxx - sx * sx;
  if (n === 0) return { a: NaN, b: 0 };
  if (Math.abs(d) < 1e-300) return { a: sy / n, b: 0 };
  const b = (n * sxy - sx * sy) / d;
  return { a: (sy - b * sx) / n, b };
}

function edgeIndices(n: number, frac: number): { head: number[]; tail: number[] } {
  const k = Math.max(1, Math.round(n * Math.min(Math.max(frac, 0), 0.5)));
  const head = Array.from({ length: Math.min(k, n) }, (_, i) => i);
  const tail = Array.from({ length: Math.min(k, n) }, (_, i) => n - 1 - i);
  return { head, tail };
}

export function computeBaseline(x: ArrayLike<number>, y: ArrayLike<number>, s: MetricSettings): Baseline {
  const n = y.length;
  switch (s.baseline) {
    case 'zero':
      return { a: 0, slope: 0, mode: 'zero' };
    case 'manual':
      return { a: s.manualBaseline, slope: 0, mode: 'manual' };
    case 'min': {
      const st = basicStats(y);
      return { a: s.polarity === 'peak' ? st.min : st.max, slope: 0, mode: 'min' };
    }
    case 'percentile': {
      const v = finiteValues(y);
      const p = s.polarity === 'peak' ? s.baselinePercentile : 100 - s.baselinePercentile;
      return { a: percentileInPlace(v, p), slope: 0, mode: 'percentile' };
    }
    case 'edges':
    case 'edgesLinear': {
      const { head, tail } = edgeIndices(n, s.edgeFraction);
      const idx = [...head, ...tail];
      if (s.baseline === 'edges') {
        const vals = idx.map((i) => y[i]).filter(Number.isFinite);
        return { a: vals.length ? vals.reduce((p, q) => p + q, 0) / vals.length : NaN, slope: 0, mode: 'edges' };
      }
      const fit = linearFit(
        idx.map((i) => x[i]),
        idx.map((i) => y[i]),
      );
      return { a: fit.a, slope: fit.b, mode: 'edgesLinear' };
    }
  }
}

/** Interpolated x where the segment (x0,y0)-(x1,y1) crosses level L. */
function cross(x0: number, y0: number, x1: number, y1: number, L: number): number {
  if (y1 === y0) return (x0 + x1) / 2;
  return x0 + ((L - y0) / (y1 - y0)) * (x1 - x0);
}

/**
 * Crossing of level L between samples a and a+1, refined with a cubic
 * Lagrange interpolant through the four surrounding samples (falls back to
 * linear interpolation at the ends or if the cubic does not bracket L).
 */
function crossCubic(x: ArrayLike<number>, s: ArrayLike<number>, a: number, L: number): number {
  const lin = cross(x[a], s[a], x[a + 1], s[a + 1], L);
  if (a < 1 || a + 2 >= s.length) return lin;
  const xs = [x[a - 1], x[a], x[a + 1], x[a + 2]];
  const ys = [s[a - 1], s[a], s[a + 1], s[a + 2]];
  const f = (t: number) => {
    let r = 0;
    for (let i = 0; i < 4; i++) {
      let w = 1;
      for (let j = 0; j < 4; j++) if (j !== i) w *= (t - xs[j]) / (xs[i] - xs[j]);
      r += w * ys[i];
    }
    return r - L;
  };
  let lo = x[a];
  let hi = x[a + 1];
  let flo = f(lo);
  if (flo * f(hi) > 0) return lin;
  for (let i = 0; i < 50; i++) {
    const mid = (lo + hi) / 2;
    const fm = f(mid);
    if (fm * flo > 0) {
      lo = mid;
      flo = fm;
    } else hi = mid;
  }
  return (lo + hi) / 2;
}

/**
 * Find local maxima of s (NaN-free) with their topographic prominence
 * (same definition as scipy.signal.peak_prominences).
 */
export function findPeaks(s: ArrayLike<number>): { index: number; prominence: number }[] {
  const n = s.length;
  const peaks: { index: number; prominence: number }[] = [];
  let i = 1;
  while (i < n - 1) {
    if (s[i] > s[i - 1]) {
      // Handle plateaus: advance while equal.
      let j = i;
      while (j + 1 < n && s[j + 1] === s[i]) j++;
      if (j + 1 < n && s[j + 1] < s[i]) {
        const mid = (i + j) >> 1;
        peaks.push({ index: mid, prominence: 0 });
      }
      i = j + 1;
    } else i++;
  }
  for (const p of peaks) {
    const h = s[p.index];
    let lmin = h;
    for (let k = p.index - 1; k >= 0; k--) {
      if (s[k] > h) break;
      if (s[k] < lmin) lmin = s[k];
    }
    let rmin = h;
    for (let k = p.index + 1; k < n; k++) {
      if (s[k] > h) break;
      if (s[k] < rmin) rmin = s[k];
    }
    p.prominence = h - Math.max(lmin, rmin);
  }
  return peaks;
}

/** Full width at `level` (fraction of height above baseline) around the peak at index p. */
export function widthAt(
  x: ArrayLike<number>,
  s: ArrayLike<number>,
  p: number,
  base: (x: number) => number,
  level: number,
  peakValue?: number,
): WidthResult {
  const peakV = peakValue ?? s[p];
  const b = base(x[p]);
  const L = b + level * (peakV - b);
  if (!(peakV > b)) return { level, value: L, left: null, right: null, width: null };
  let left: number | null = null;
  for (let k = p; k > 0; k--) {
    if (s[k - 1] <= L) {
      left = crossCubic(x, s, k - 1, L);
      break;
    }
  }
  let right: number | null = null;
  for (let k = p; k < s.length - 1; k++) {
    if (s[k + 1] <= L) {
      right = crossCubic(x, s, k, L);
      break;
    }
  }
  return { level, value: L, left, right, width: left !== null && right !== null ? Math.abs(right - left) : null };
}

/**
 * Sub-sample peak vertex from the parabola through samples p-1, p, p+1
 * (uniform spacing assumed). Returns the sample itself at the ends or on plateaus.
 */
export function refinePeak(x: ArrayLike<number>, s: ArrayLike<number>, p: number): { x: number; y: number } {
  if (p < 1 || p >= s.length - 1) return { x: x[p], y: s[p] };
  const y0 = s[p - 1];
  const y1 = s[p];
  const y2 = s[p + 1];
  const den = y0 - 2 * y1 + y2;
  if (!(den < 0)) return { x: x[p], y: y1 };
  const d = Math.max(-0.5, Math.min(0.5, (0.5 * (y0 - y2)) / den));
  const dx = d >= 0 ? x[p + 1] - x[p] : x[p] - x[p - 1];
  return { x: x[p] + d * dx, y: y1 - 0.25 * (y0 - y2) * d };
}

/** Keep only finite samples (x and y) so peak/width searches never straddle a gap. */
function compact(x: ArrayLike<number>, y: ArrayLike<number>) {
  const xs: number[] = [];
  const ys: number[] = [];
  const idx: number[] = [];
  for (let i = 0; i < y.length; i++) {
    if (Number.isFinite(y[i]) && Number.isFinite(x[i])) {
      xs.push(x[i]);
      ys.push(y[i]);
      idx.push(i);
    }
  }
  return { xs, ys, idx };
}

export function computeEdge(x: ArrayLike<number>, y: ArrayLike<number>, s: MetricSettings): EdgeResult | null {
  const { xs, ys } = compact(x, y);
  const n = ys.length;
  if (n < 4) return null;
  const { head, tail } = edgeIndices(n, s.edgeFraction);
  const lowSide = head.reduce((p, i) => p + ys[i], 0) / head.length;
  const highSide = tail.reduce((p, i) => p + ys[i], 0) / tail.length;
  const rising = highSide >= lowSide;
  const lo = Math.min(lowSide, highSide);
  const hi = Math.max(lowSide, highSide);
  if (!(hi > lo)) return null;
  // Normalised signal that rises from 0 to 1 along increasing index.
  const q = ys.map((v) => (rising ? (v - lo) / (hi - lo) : (hi - v) / (hi - lo)));
  let maxSlope = 0;
  let c = 0;
  for (let i = 1; i < n; i++) {
    const d = (q[i] - q[i - 1]) / (xs[i] - xs[i - 1] || 1);
    if (d > maxSlope) {
      maxSlope = d;
      c = i;
    }
  }
  const findBack = (L: number): number | null => {
    for (let k = c; k > 0; k--) if (q[k - 1] <= L && q[k] >= L) return cross(xs[k - 1], q[k - 1], xs[k], q[k], L);
    return null;
  };
  const findFwd = (L: number): number | null => {
    for (let k = c - 1; k < n - 1; k++) if (q[k] <= L && q[k + 1] >= L) return cross(xs[k], q[k], xs[k + 1], q[k + 1], L);
    return null;
  };
  const x50 = findFwd(0.5) ?? findBack(0.5);
  const xl = findBack(s.edgeLow);
  const xh = findFwd(s.edgeHigh);
  return {
    direction: rising ? 'rising' : 'falling',
    low: lo,
    high: hi,
    x10: xl,
    x50,
    x90: xh,
    distance: xl !== null && xh !== null ? Math.abs(xh - xl) : null,
    maxSlope: maxSlope * (hi - lo) * (rising ? 1 : -1),
  };
}

export function computeMetrics(x: ArrayLike<number>, y: ArrayLike<number>, s: MetricSettings, clip?: ArrayLike<number>): ProfileMetrics {
  const st = basicStats(y);
  const vals = finiteValues(y);
  const median = percentileInPlace(vals, 50);
  const baseline = computeBaseline(x, y, s);
  const base = (xx: number) => baselineAt(baseline, xx);

  let integral = 0;
  let cNum = 0;
  let cDen = 0;
  for (let i = 1; i < y.length; i++) {
    if (Number.isFinite(y[i]) && Number.isFinite(y[i - 1])) integral += ((y[i] + y[i - 1]) / 2) * (x[i] - x[i - 1]);
  }
  for (let i = 0; i < y.length; i++) {
    if (!Number.isFinite(y[i])) continue;
    const w = (y[i] - base(x[i])) * (s.polarity === 'peak' ? 1 : -1);
    cNum += w * x[i];
    cDen += w;
  }

  // Peak analysis on a sign-adjusted, gap-free signal.
  const { xs, ys } = compact(x, y);
  const sgn = s.polarity === 'peak' ? 1 : -1;
  const sig = ys.map((v) => sgn * v);
  const sigBase = (xx: number) => sgn * base(xx);
  const peaks: PeakResult[] = [];
  // A profile whose variation is at floating-point rounding level (e.g. interpolation of a
  // constant area) is flat: it has no peaks.
  const magnitude = Math.max(Math.abs(st.max), Math.abs(st.min));
  const flat = !(st.max - st.min > 1e-9 * magnitude);
  if (sig.length >= 3 && !flat) {
    let cand = findPeaks(sig);
    // A global extremum at the very edge is not a local max; still report it for 'highest'.
    let gi = 0;
    for (let i = 1; i < sig.length; i++) if (sig[i] > sig[gi]) gi = i;
    const range = st.max - st.min;
    if (s.peakMode === 'highest') {
      // Prefer the highest local maximum (plateau-aware, centred); fall back to an edge maximum.
      let best = cand[0];
      for (const p of cand) if (sig[p.index] > sig[best.index]) best = p;
      let smin = Infinity;
      for (const v of sig) if (v < smin) smin = v;
      cand = [best && sig[best.index] >= sig[gi] ? best : { index: gi, prominence: sig[gi] - smin }];
    } else {
      cand = cand.filter((p) => p.prominence >= s.minProminence * range);
      cand.sort((a, b) => sig[b.index] - sig[a.index]);
      if (s.minSeparation > 0) {
        const kept: typeof cand = [];
        for (const p of cand) if (kept.every((k) => Math.abs(xs[k.index] - xs[p.index]) >= s.minSeparation)) kept.push(p);
        cand = kept;
      }
      cand.sort((a, b) => a.index - b.index);
    }
    for (const p of cand) {
      const v = refinePeak(xs, sig, p.index);
      peaks.push({
        index: p.index,
        x: v.x,
        value: sgn * v.y,
        height: v.y - sigBase(v.x),
        prominence: p.prominence,
        widths: s.widthLevels.map((lv) => {
          const w = widthAt(xs, sig, p.index, sigBase, lv, v.y);
          return { ...w, value: sgn * w.value };
        }),
      });
    }
  }

  let clipped = 0;
  if (clip) for (let i = 0; i < clip.length; i++) if (clip[i] & 1) clipped++;

  return {
    n: y.length,
    nValid: st.n,
    min: st.min,
    max: st.max,
    xMin: st.argmin >= 0 ? x[st.argmin] : NaN,
    xMax: st.argmax >= 0 ? x[st.argmax] : NaN,
    mean: st.mean,
    sd: st.sd,
    median,
    rms: st.rms,
    integral,
    cv: st.sd / st.mean,
    michelson: (st.max - st.min) / (st.max + st.min),
    u0: st.min / st.mean,
    u1: st.min / st.max,
    nonUniformity: (st.max - st.min) / st.mean,
    centroid: cDen !== 0 ? cNum / cDen : NaN,
    baseline,
    peaks,
    edge: computeEdge(x, y, s),
    clippedSamples: clipped,
  };
}

/** Gaussian smoothing of a gap-aware signal (NaN samples are skipped and renormalised). */
export function gaussianSmooth(y: ArrayLike<number>, sigma: number): Float64Array {
  const n = y.length;
  const out = new Float64Array(n);
  if (!(sigma > 0)) {
    for (let i = 0; i < n; i++) out[i] = y[i];
    return out;
  }
  const r = Math.ceil(3 * sigma);
  const k = Array.from({ length: 2 * r + 1 }, (_, i) => Math.exp(-((i - r) ** 2) / (2 * sigma * sigma)));
  for (let i = 0; i < n; i++) {
    if (!Number.isFinite(y[i])) {
      out[i] = NaN;
      continue;
    }
    let s = 0;
    let w = 0;
    for (let j = -r; j <= r; j++) {
      const v = y[i + j];
      if (i + j < 0 || i + j >= n || !Number.isFinite(v)) continue;
      s += v * k[j + r];
      w += k[j + r];
    }
    out[i] = s / w;
  }
  return out;
}

/** dy/dx by central differences (one-sided at ends and next to gaps). */
export function derivative(x: ArrayLike<number>, y: ArrayLike<number>, sigma = 0): Float64Array {
  const ys = gaussianSmooth(y, sigma);
  const n = ys.length;
  const d = new Float64Array(n);
  for (let i = 0; i < n; i++) {
    const okL = i > 0 && Number.isFinite(ys[i - 1]);
    const okR = i < n - 1 && Number.isFinite(ys[i + 1]);
    if (!Number.isFinite(ys[i])) d[i] = NaN;
    else if (okL && okR) d[i] = (ys[i + 1] - ys[i - 1]) / (x[i + 1] - x[i - 1]);
    else if (okR) d[i] = (ys[i + 1] - ys[i]) / (x[i + 1] - x[i]);
    else if (okL) d[i] = (ys[i] - ys[i - 1]) / (x[i] - x[i - 1]);
    else d[i] = NaN;
  }
  return d;
}
